import React, { useEffect } from 'react';
import { RefreshUniversal } from '@duxton-web/icons';
import { Alert, Button, Select, Table, Typography } from '@duxton-web/ui';
import { useQueryClient } from '@tanstack/react-query';

import { isProduction } from '@/common/environment';
import { useVariableContext } from '@/features/variable/contexts/variable-context';
import Statistics from '@/features/variable/settings/stats/statistics';
import { queryKeys, useVariableStats } from '@/hooks';
import { ScopeStatsView } from '@/lib/App';

const Stats = () => {
  const { variable } = useVariableContext();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useVariableStats(variable?.name, {
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const [selectedScope, setSelectedScope] = React.useState<string>('');
  const [scopeData, setScopeData] = React.useState<{
    columns: { title: string; dataIndex: string; key: string }[];
    data: { name: string; count: number; key: string }[];
  }>({
    columns: [],
    data: [],
  });

  useEffect(() => {
    if (isLoading) return;

    const stats = data?.data;
    const scopes = new Map(Object.entries(stats?.scopes || {}));
    const firstScope = scopes.keys().next().value;

    const scopeData = getScopeData(stats);
    const defaultScope =
      scopeData.data.length > 0 ? scopeData.data[0].name : '';

    setSelectedScope(firstScope || defaultScope);
    setScopeData(scopeData);
  }, [data, isLoading, error]);

  const handleScopeChange = (value) => {
    setSelectedScope(value);
  };

  const getScopeData = (stats) => {
    let data: { name: string; count: number; key: string }[] = [];
    let scopes = new Map(Object.entries(stats?.scopes || {}));
    scopes.forEach((value: ScopeStatsView, key: string) => {
      data.push({
        name: key,
        count: value.count,
        key: key,
      });
    });

    return {
      columns: [
        {
          title: 'Scope',
          dataIndex: 'name',
          key: 'name',
          sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
          title: 'Count(1hr)',
          dataIndex: 'count',
          key: 'count',
          sorter: (a, b): number => a.count - b.count,
        },
      ],
      data: data,
    };
  };

  if (!isProduction) {
    return (
      <Alert
        message="Stats Under Maintenance"
        description="Variable stats are currently under maintenance due to staging cluster migration. This service will be restored once the migration is complete."
        type="info"
        showIcon
        closable={false}
      />
    );
  }

  const stats = data?.data;

  return (
    <div className="variable-stats-component">
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          {stats?.data_at && (
            <Typography.Text>
              Data At: {new Date(stats?.data_at).toString()}
            </Typography.Text>
          )}
          {stats?.updated_at && (
            <Typography.Text>
              Data is updated at {new Date(stats?.updated_at).toString()}
            </Typography.Text>
          )}
        </div>

        <Button
          shape="circle"
          size="small"
          type="secondary"
          disabled={isLoading}
          icon={<RefreshUniversal />}
          onClick={() =>
            queryClient.invalidateQueries({
              queryKey: queryKeys.planner.variableStats(variable?.name),
            })
          }
        />
      </div>

      <div className="variable-stats-component__header">
        {!stats?.data_at && (
          <div>
            No data available in recent hours! Click reload button to check
            again. <br />
            Stats usually becomes available after 2 hours when there is traffic
            to the variable usage in your service.
          </div>
        )}
      </div>
      <Table
        className="variable-stats-component__scopes"
        dataSource={scopeData.data}
        columns={scopeData.columns}
        showHeader={true}
        pagination={false}
        size="small"
      />
      <div className="variable-stats-component__scopes-details">
        <Typography.Title level={3}>Details by Scope</Typography.Title>
        <Select
          data-testid="scope-select"
          defaultValue={selectedScope}
          value={selectedScope}
          style={{ width: '100%' }}
          onChange={handleScopeChange}
        >
          {scopeData.data.map((sc) => (
            <Select.Option key={sc.name} value={sc.name}>
              {sc.name}
            </Select.Option>
          ))}
        </Select>
        {stats?.scopes?.[selectedScope] && (
          <Statistics scopeStats={stats?.scopes?.[selectedScope]} />
        )}
      </div>
    </div>
  );
};

export default Stats;
